import { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { IMaskMixin } from 'react-imask';
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Table,
  TableBody,
  TableRow,
  Typography,
} from '@mui/material';
import { lightGreen } from '@mui/material/colors';
import EditIcon from '@mui/icons-material/Edit';

import { SetDeposit } from 'store/actions/schoolDetailActions';

import BookMaterialComponent from 'components/SchoolBookMaterialComponent';
import PriceComponent from 'components/PriceComponent';
import RemainingBalanceInfoComponent from 'components/SummaryComponent/RemainingBalanceInfoComponent';
import StyledTableCell from 'components/SummaryComponent/StyledTableCell';
import SummaryInfoComponent from 'components/SummaryComponent/SummaryTotalValueComponent/SummaryInfoComponent';
import AccommodationFee from 'components/SummaryComponent/SummaryContentComponent/CardAccommodationComponent/AccommodationFee';

import { bankersRounding } from 'utils/payment/bankersRoundingUtils';

const MaskedInput = IMaskMixin(({ inputRef, ...props }) => (
  <Box mx={1}>
    <OutlinedInput {...props} inputRef={inputRef} />
  </Box>
));

function SummaryTotalValue({
  currency,
  deposit,
  header,
  minimumPayment,
  setDeposit,
  showAccommodationFee,
  summaryItems,
  totalDiscount,
  totalDiscountExchanged,
}) {
  const dispatch = useDispatch();
  const numberFormatOptionsCustom = {
    currency,
    style: 'currency',
  };

  const remainingValue = bankersRounding(totalDiscountExchanged - deposit, 2);
  const [customDeposit, setCustomDeposit] = useState(0);
  const [openCustomDeposit, setOpenCustomDeposit] = useState(false);

  const displayRegistrationFee = () => {
    const hasDiscount =
      summaryItems?.registrationFeeExchanded >
      summaryItems?.registrationFeeWithDiscountExchanged;

    const currentPrice = hasDiscount
      ? summaryItems?.registrationFeeWithDiscountExchanged
      : summaryItems?.registrationFeeExchanded;

    return summaryItems?.registrationFeeExchanded > 0 &&
      !Number.isNaN(summaryItems?.registrationFeeExchanded) ? (
      <TableRow key="matricula">
        <StyledTableCell component="th" scope="row">
          Matrícula
        </StyledTableCell>

        <StyledTableCell align="right">
          {hasDiscount && (
            <Typography
              component="span"
              sx={{
                color: '#8a8a8a',
                marginRight: '8px',
                textAlign: 'right',
                textDecoration: 'line-through',
              }}
              variant="caption"
            >
              <PriceComponent value={summaryItems?.registrationFeeExchanded} />
            </Typography>
          )}

          <Typography
            component="span"
            sx={{ textAlign: 'right', fontWeight: 900 }}
            variant="caption"
          >
            {currentPrice > 0 ? (
              <PriceComponent value={currentPrice} />
            ) : (
              <strong>GRÁTIS</strong>
            )}
          </Typography>
        </StyledTableCell>
      </TableRow>
    ) : null;
  };

  return (
    <>
      <Box my={1}>
        <Divider />
      </Box>

      <Table>
        <TableBody>
          {displayRegistrationFee()}

          {showAccommodationFee && (
            <TableRow key="accommodationFee">
              <StyledTableCell component="th" scope="row">
                Taxa de acomodação
              </StyledTableCell>

              <StyledTableCell align="right">
                <AccommodationFee registrationFee={summaryItems} />
              </StyledTableCell>
            </TableRow>
          )}

          <BookMaterialComponent summaryItems={summaryItems} />

          {summaryItems?.accommodationSupplementExchanged > 0 &&
          !Number.isNaN(summaryItems?.accommodationSupplementExchanged) ? (
            <SummaryInfoComponent
              key="accommodationSupplementExchanged"
              title="Suplemento da acomodação"
              value={summaryItems?.accommodationSupplementExchanged}
            />
          ) : null}

          {summaryItems?.accommodations &&
          summaryItems?.accommodations[0]?.supplementPriceExchanged > 0 &&
          !Number.isNaN(
            summaryItems?.accommodations[0]?.supplementPriceExchanged
          ) ? (
            <SummaryInfoComponent
              key="accommodationSupplementExchanged"
              title="Suplemento da acomodação"
              value={summaryItems?.accommodations[0]?.supplementPriceExchanged}
            />
          ) : null}

          {summaryItems?.courseSupplementExchanged > 0 &&
          !Number.isNaN(summaryItems?.courseSupplementExchanged) ? (
            <SummaryInfoComponent
              key="courseSupplementExchanged"
              title="Suplemento do curso"
              value={summaryItems?.courseSupplementExchanged}
            />
          ) : null}

          {summaryItems?.courses &&
          summaryItems?.courses[0]?.supplementPriceExchanged > 0 &&
          !Number.isNaN(summaryItems?.courses[0]?.supplementPriceExchanged) ? (
            <SummaryInfoComponent
              key="courseSupplementExchanged"
              title="Suplemento do curso"
              value={summaryItems?.courses[0]?.supplementPriceExchanged}
            />
          ) : null}

          <TableRow key="Taxa de agenciamento">
            <StyledTableCell component="th" scope="row">
              <Box>Taxa de Agenciamento</Box>
            </StyledTableCell>

            <StyledTableCell align="right">
              <strong>GRÁTIS</strong>
            </StyledTableCell>
          </TableRow>

          <TableRow key="Suporte e Assessoria">
            <StyledTableCell component="th" scope="row">
              Suporte e Assessoria
            </StyledTableCell>

            <StyledTableCell align="right">
              <strong>GRÁTIS</strong>
            </StyledTableCell>
          </TableRow>

          {summaryItems?.discount > 0 &&
          !Number.isNaN(summaryItems?.discount) ? (
            <TableRow key="Valor Total">
              <StyledTableCell component="th" scope="row">
                Valor total
              </StyledTableCell>

              <StyledTableCell align="right">
                <Typography
                  component="div"
                  sx={{
                    color: '#8a8a8a',
                    textAlign: 'right',
                    textDecoration: 'line-through',
                  }}
                  variant="body2"
                >
                  {summaryItems?.discount > 0 &&
                  summaryItems?.totalExchanged > 0 &&
                  !Number.isNaN(summaryItems?.totalExchanged) &&
                  !Number.isNaN(summaryItems?.discount) ? (
                    <PriceComponent value={summaryItems?.totalExchanged} />
                  ) : null}
                </Typography>
              </StyledTableCell>
            </TableRow>
          ) : null}

          {summaryItems?.discount > 0 &&
          !Number.isNaN(summaryItems?.discount) ? (
            <TableRow key="DESCONTO Fluencypass">
              <StyledTableCell component="th" scope="row">
                <Typography variant="body2" component="div">
                  <Box fontWeight="bold" fontSize={14}>
                    DESCONTO Fluencypass
                  </Box>
                </Typography>
              </StyledTableCell>

              <StyledTableCell sx={{ color: lightGreen[800] }} align="right">
                <Typography
                  component="div"
                  sx={{ textAlign: 'right', fontWeight: 'bold' }}
                  variant="body2"
                >
                  {summaryItems?.discount > 0 &&
                  !Number.isNaN(summaryItems?.discount) ? (
                    <>
                      <Box component="span">-</Box>
                      <PriceComponent value={summaryItems?.discount} />
                    </>
                  ) : (
                    <PriceComponent value={0} />
                  )}
                </Typography>
              </StyledTableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>

      <Box textAlign="left" mt={2} mx={2}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={6}>
            <Typography
              component="div"
              sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}
              variant="body2"
            >
              Valor total
              {summaryItems?.discount > 0 &&
                !Number.isNaN(summaryItems?.discount) && (
                  <Box component="span">&nbsp;com desconto</Box>
                )}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 'bold',
                textAlign: 'right',
                textTransform: 'uppercase',
              }}
            >
              {totalDiscountExchanged &&
              !Number.isNaN(totalDiscountExchanged) ? (
                <PriceComponent value={totalDiscountExchanged} />
              ) : (
                <Box>-</Box>
              )}
            </Typography>

            {remainingValue > 0 && !Number.isNaN(remainingValue) && (
              <Typography
                component="div"
                sx={{ textAlign: 'right' }}
                variant="caption"
              >
                {totalDiscount && !Number.isNaN(totalDiscount) ? (
                  <Box>
                    (
                    <PriceComponent
                      value={totalDiscount}
                      numberFormat={numberFormatOptionsCustom}
                    />
                    )
                  </Box>
                ) : null}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>

      {remainingValue > 0 && !Number.isNaN(remainingValue) && (
        <>
          <Box my={2}>
            <Divider />
          </Box>
          <Box textAlign="left" mt={2} mx={2}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  component="div"
                  sx={{ textTransform: 'uppercase' }}
                >
                  <Box textAlign="center" fontWeight="bold">
                    Depósito mínimo:&nbsp;
                    <PriceComponent value={minimumPayment || 1} />
                  </Box>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box mb={2}>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{ textTransform: 'uppercase' }}
                  >
                    <Box textAlign="center" fontWeight="bold" fontSize={16}>
                      {header}
                    </Box>
                  </Typography>
                  <Typography variant="h4" component="div">
                    <Box textAlign="center" fontWeight="bold">
                      {deposit && !Number.isNaN(deposit) ? (
                        <Box>
                          {!openCustomDeposit ? (
                            <Collapse in={!openCustomDeposit}>
                              <PriceComponent value={deposit} />
                              <IconButton
                                size="small"
                                color="inherit"
                                aria-label="settings"
                                onClick={() => {
                                  const depositRounded = Math.ceil(deposit);
                                  setCustomDeposit(depositRounded);
                                  setOpenCustomDeposit(!openCustomDeposit);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Collapse>
                          ) : (
                            <Box py={2}>
                              <FormControl variant="outlined">
                                <MaskedInput
                                  id="deposit"
                                  name="deposit"
                                  startAdornment={
                                    <InputAdornment position="start">
                                      R$
                                    </InputAdornment>
                                  }
                                  mask={Number}
                                  radix="."
                                  mapToRadix={[',']}
                                  max={totalDiscountExchanged + 0.01}
                                  value={customDeposit.toString()}
                                  scale={2}
                                  onAccept={(val) => {
                                    setCustomDeposit(Number(val));
                                  }}
                                />
                              </FormControl>
                              <Box>
                                <Button
                                  size="large"
                                  color="primary"
                                  variant="contained"
                                  onClick={() => {
                                    const valueToUpdate =
                                      customDeposit > minimumPayment
                                        ? customDeposit
                                        : minimumPayment;

                                    if (customDeposit > 0) {
                                      setDeposit(valueToUpdate);
                                      SetDeposit(dispatch, valueToUpdate);
                                      setOpenCustomDeposit(!openCustomDeposit);
                                    }
                                  }}
                                >
                                  Atualizar
                                </Button>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      ) : (
                        <Box>-</Box>
                      )}
                    </Box>
                  </Typography>
                  <Typography variant="body2" component="div">
                    {deposit && !Number.isNaN(deposit) ? (
                      <Box textAlign="center">
                        (
                        <PriceComponent
                          value={deposit / summaryItems.exchangeRate}
                          numberFormat={numberFormatOptionsCustom}
                        />
                        )
                      </Box>
                    ) : null}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Box mb={2}>
              {remainingValue > 0 && (
                <RemainingBalanceInfoComponent
                  value={
                    <PriceComponent
                      value={remainingValue / summaryItems.exchangeRate}
                      numberFormat={numberFormatOptionsCustom}
                    />
                  }
                />
              )}
            </Box>
          </Box>
        </>
      )}
    </>
  );
}

SummaryTotalValue.propTypes = {
  currency: PropTypes.string,
  deposit: PropTypes.number,
  header: PropTypes.string,
  minimumPayment: PropTypes.number,
  setDeposit: PropTypes.func,
  summaryItems: PropTypes.instanceOf(Object).isRequired,
  totalDiscount: PropTypes.number,
  totalDiscountExchanged: PropTypes.number,
};

SummaryTotalValue.defaultProps = {
  currency: '$',
  deposit: 0,
  header: 'Reserve agora por apenas',
  minimumPayment: 0,
  setDeposit: () => {},
  totalDiscount: 0,
  totalDiscountExchanged: 0,
};

export default SummaryTotalValue;
